import React, { useState, useEffect, useMemo, useCallback, useContext } from 'react';
import { Box, Grid, Button, CircularProgress, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { tokens } from '../../theme';
import { useDispatch, useSelector } from 'react-redux';
import AddIcon from '@mui/icons-material/Add';
import DisciplineCard from '../../SystemDesign/DisciplineCard';
import UnitCard from '../../SystemDesign/UnitCard';
import AddDiscipline from './AddDiscipline';
import DeleteDiscipline from './DeleteDiscipline';
import UpdateDiscipline from './UpdateDiscipline';
import DeleteUnit from './Units/DeleteUnit';
import UpdateUnit from './Units/UpdateUnit';
import { useGetDisciplinesQuery, useGetUnitsQuery } from '../../services/SkillsManagement';
import { INITIALIZE_DISCIPLINES, INITIALIZE_UNITS, CLEAR_DISCIPLINES_LIST, CLEAR_UNITS_LIST } from '../../Reducers/Actions';
import { isEmpty } from '../../Utils/Functions';
import { TitleScreen, SubTitleScreen, PrimaryButton, SecondaryOutlinedButton } from '../../SystemDesign/Styles';
import LoaderSpinner from '../../SystemDesign/LoaderSpinner';
import AddUnit from "./Units/AddUnit";
import PaginationUI from '../../SystemDesign/PaginationUI';
import SearchInput from '../../SystemDesign/SearchInput';
import { AuthContext } from '../../Context/AuthContext';

const DisciplineList = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const dispatch = useDispatch();
  const { ability } = useContext(AuthContext);

  const disciplinesFromStore = useSelector(state => state.DisciplineReducer);
  const unitsFromStore = useSelector(state => state.UnitReducer);

  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openAddUnitDialog, setOpenAddUnitDialog] = useState(false);
  const [openDeleteUnitDialog, setOpenDeleteUnitDialog] = useState(false);
  const [openUpdateUnitDialog, setOpenUpdateUnitDialog] = useState(false);
  const [selectedDiscipline, setSelectedDiscipline] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");


  const [queryParams, setQueryParams] = useState(() => {
    // Check localStorage for saved rowsPerPage value
    const savedRowsPerPage = localStorage.getItem('disciplines-rowsPerPage');
    const rowsPerPage = savedRowsPerPage !== null ? parseInt(savedRowsPerPage, 10) : 10;

    return {
      filters: "",
      sorting: "",
      pagination: JSON.stringify({ currentPage: 1, rowsPerPage: rowsPerPage }),
      search: ""
    };
  });

  // Debug current queryParams
  useEffect(() => {
  }, [queryParams]);


  const canReadDiscipline = Boolean(ability?.can('manage', 'all') || ability?.can('read', 'Discipline') || ability?.can('manage', 'Discipline'));
  const canReadUnit = Boolean(ability?.can('manage', 'all') || ability?.can('read', 'Unit') || ability?.can('manage', 'Unit'));

  const { data: disciplinesResponse = {}, isLoading, isError, error, refetch, isFetching } = useGetDisciplinesQuery(queryParams, {
    refetchOnMountOrArgChange: true,
    skip: !canReadDiscipline,
  });


  const { data: unitsResponse = {}, isLoading: unitsLoading, isError: unitsError } = useGetUnitsQuery({
    pagination: JSON.stringify({ currentPage: 1, rowsPerPage: 100 }),
    sorting: ""
  }, {
    refetchOnMountOrArgChange: true,
    skip: !canReadUnit,
  });

  // Log the structure we receive to help debug
  // Extract units from response data
  const units = unitsResponse?.data?.units ||
    unitsResponse?.units ||
    unitsResponse?.rows ||
    (Array.isArray(unitsResponse) ? unitsResponse : []);

  // Initialize units in Redux store - similar pattern as disciplines
  const [hasInitializedUnitsStore, setHasInitializedUnitsStore] = useState(false);

  useEffect(() => {
    // Only initialize units from API on first load
    const shouldInitUnitsFromAPI =
      units &&
      Array.isArray(units) &&
      units.length > 0 &&
      !unitsError &&
      !hasInitializedUnitsStore &&
      (!Array.isArray(unitsFromStore) || unitsFromStore.length === 0);

    if (shouldInitUnitsFromAPI) {
      // First clear the existing units list
      dispatch({ type: CLEAR_UNITS_LIST });
      // Then initialize with new data
      dispatch({ type: INITIALIZE_UNITS, payload: units });

      // Mark that we've initialized the units store
      setHasInitializedUnitsStore(true);
    }
  }, [units, dispatch, unitsError, unitsFromStore, hasInitializedUnitsStore]);


  const disciplines = useMemo(() => {
    return disciplinesResponse?.data?.disciplines ||
      disciplinesResponse?.disciplines ||
      disciplinesResponse?.rows ||
      (Array.isArray(disciplinesResponse) ? disciplinesResponse : []);
  }, [disciplinesResponse]);


  const paginationInfo = disciplinesResponse?.data?.pagination ||
    disciplinesResponse?.pagination || {
    currentPage: parseInt(JSON.parse(queryParams.pagination || '{}').currentPage || 1, 10),
    rowsPerPage: parseInt(JSON.parse(queryParams.pagination || '{}').rowsPerPage || 10, 10),
    total: Array.isArray(disciplines) ? disciplines.length : 0,
    page: parseInt(JSON.parse(queryParams.pagination || '{}').currentPage || 1, 10),
    limit: parseInt(JSON.parse(queryParams.pagination || '{}').rowsPerPage || 10, 10),
    totalPages: Math.ceil((Array.isArray(disciplines) ? disciplines.length : 0) /
      parseInt(JSON.parse(queryParams.pagination || '{}').rowsPerPage || 10, 10))
  };


  const normalizedPaginationInfo = {
    currentPage: paginationInfo.page || paginationInfo.currentPage,
    rowsPerPage: paginationInfo.limit || paginationInfo.rowsPerPage,
    total: paginationInfo.total || 0
  };

  // Only initialize Redux store with API data on first load
  const [hasInitializedStore, setHasInitializedStore] = useState(false);

  useEffect(() => {
    // Only initialize Redux store from API data when:
    // 1. We haven't initialized it yet
    // 2. We have API data
    // 3. There's no error with the API
    // 4. Redux store is empty
    const shouldInitFromAPI =
      !hasInitializedStore &&
      Array.isArray(disciplines) &&
      disciplines.length > 0 &&
      !isError &&
      (!Array.isArray(disciplinesFromStore) || disciplinesFromStore.length === 0);

    if (shouldInitFromAPI) {

      // Process disciplines to ensure consistent format
      const processedDisciplines = disciplines.map(discipline => {
        const disciplineId = discipline.discipline_id || discipline.id;
        return {
          discipline_id: disciplineId,
          name: discipline.name || '',
          icon: discipline.icon, // Preserve the icon data from backend
          createdAt: discipline.createdAt || new Date().toISOString(),
          updatedAt: discipline.updatedAt || new Date().toISOString(),
          unitCount: discipline.unitCount || 0,
          //skillCount: discipline.skillCount || 0,
          color: discipline.color || getDefaultColor()
        };
      });

      // Dispatch the complete discipline objects to Redux
      dispatch({ type: CLEAR_DISCIPLINES_LIST });
      dispatch({ type: INITIALIZE_DISCIPLINES, payload: processedDisciplines });

      setHasInitializedStore(true);
    }
  }, [disciplines, dispatch, isError, disciplinesFromStore, hasInitializedStore]);

  // Use default color if color is not provided
  const getDefaultColor = () => {
    return '#2196F3'; // Default blue
  };




  const enhancedDisciplines = useMemo(() => {
    // Always render using the latest API response (server-side pagination)
    const disciplinesToUse = Array.isArray(disciplines) ? disciplines : [];

    // First, deduplicate disciplines by discipline_id to prevent duplicates
    const uniqueDisciplinesMap = {};
    disciplinesToUse.forEach(discipline => {
      const id = discipline.discipline_id || discipline.id;
      // Keep only the first occurrence of each discipline ID
      if (!uniqueDisciplinesMap[id]) {
        uniqueDisciplinesMap[id] = discipline;
      }
    });

    // Convert back to array and enhance
    const uniqueDisciplines = Object.values(uniqueDisciplinesMap);

    const enhanced = uniqueDisciplines.map(discipline => {
      const id = discipline.discipline_id || discipline.id;
      return {
        ...discipline,
        discipline_id: id,
        color: discipline.color || getDefaultColor()
      };
    });

    return enhanced;
  }, [disciplines]);


  const applyFilters = (filters = null, paginationInfo = null, sortingInfo = null, searchValue = null) => {
    // Start with current params
    const newParams = { ...queryParams };

    // Apply filters if provided
    if (filters !== null) {
      newParams.filters = JSON.stringify(filters);
    } else {
      // Keep existing filters
      newParams.filters = queryParams.filters || "";
    }

    // Apply pagination if provided
    if (paginationInfo !== null) {
      // Get current pagination
      const currentPagination = newParams.pagination
        ? JSON.parse(newParams.pagination)
        : { currentPage: 1, rowsPerPage: 10 };

      // Merge with new pagination info
      const updatedPagination = { ...currentPagination, ...paginationInfo };

      // Save rowsPerPage to localStorage
      if (paginationInfo.rowsPerPage) {
        localStorage.setItem('disciplines-rowsPerPage', paginationInfo.rowsPerPage.toString());
      }

      // Update pagination in params
      newParams.pagination = JSON.stringify(updatedPagination);
    }

    // Apply sorting if provided
    if (sortingInfo !== null) {
      newParams.sorting = sortingInfo;
    }

    // Apply search if provided
    if (searchValue !== null) {
      newParams.search = searchValue;
    }
    setQueryParams(newParams);
  };

  // Handle search functionality
  const handleSearch = (value) => {
    setSearchTerm(value);

    // Create new queryParams object with search value
    const newParams = {
      ...queryParams,
      search: value,
      pagination: JSON.stringify({
        ...JSON.parse(queryParams.pagination || '{}'),
        currentPage: 1 // Reset to page 1 when searching
      })
    };

    // Update query params directly to trigger API call
    setQueryParams(newParams);
  };

  // Note: enhancedDisciplines is already defined above with useMemo

  // Handle event handlers for discipline cards
  const handleEditDiscipline = useCallback((discipline) => {
    setSelectedDiscipline(discipline);
    setOpenEditDialog(true);

  }, []);

  const handleDeleteDiscipline = useCallback((discipline) => {
    setSelectedDiscipline(discipline);
    setOpenDeleteDialog(true);
  }, []);

  const handleDisciplineUpdated = useCallback(() => {
    setOpenEditDialog(false);
    refetch();
  }, [refetch]);

  const handleDisciplineDeleted = useCallback(() => {
    setOpenDeleteDialog(false);
    refetch();
  }, [refetch]);

  // Handle Unit events
  const handleEditUnit = useCallback((unit) => {
    setSelectedUnit(unit);
    setOpenUpdateUnitDialog(true);
  }, []);

  const handleDeleteUnit = useCallback((unit) => {
    setSelectedUnit(unit);
    setOpenDeleteUnitDialog(true);
  }, []);

  const handleUnitDeleted = useCallback(() => {
    setOpenDeleteUnitDialog(false);
    refetch();
  }, [refetch]);

  const handleUnitUpdated = useCallback(() => {
    setOpenUpdateUnitDialog(false);
    refetch();
  }, [refetch]);

  const handleAddDiscipline = useCallback(() => {
    setOpenAddDialog(false);
    refetch();
  }, [refetch]);

  // Memoize the click handler to prevent recreation on each render
  const handleCardClick = React.useCallback((discipline) => {
  }, []);

  // Render functions
  const renderContent = () => {
    if (isLoading && !isFetching) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
          {/* <CircularProgress /> */}
        </Box>
      );
    }

    if (isError) {
      // Get more detailed error information
      const errorMessage = error?.data?.message ||
        (error?.status ? `Status ${error.status}: ${error.error || 'API Error'}` : 'Unknown error');

      console.error('Departments fetch error:', error);

      return (
        <Box sx={{ textAlign: 'center', py: 5 }}>
          <Typography color="error" variant="h6" gutterBottom>
            Error loading departments
          </Typography>
          <Typography color="error" variant="body1" sx={{ mb: 2 }}>
            {errorMessage}
          </Typography>
        </Box>
      );
    }

    // Server-side pagination: render the current page from API response
    const paginatedDisciplines = enhancedDisciplines;

    // Show loading indicator when fetching more data but already have some content
    const showContent = (
      <>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
          {paginatedDisciplines.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 5 }}>
              <Typography variant="h6" color={colors.grey[500]} mb={2}>
                No departments found
              </Typography>
            </Box>
          ) : (
            paginatedDisciplines.map((discipline) => {
              const disciplineId = discipline.discipline_id || discipline.id || `temp-${Math.random()}`;
              // Filter units that belong to this discipline - handle potential ID format differences
              const disciplineUnits = unitsFromStore.filter(unit => {
                const unitDisciplineId = unit.discipline_id || unit.disciplineId;
                return unitDisciplineId === disciplineId;
              });

              return (
                <Box key={disciplineId} sx={{ width: '100%' }}>
                  <DisciplineCard
                    discipline={{
                      ...discipline,
                      discipline_id: disciplineId // Ensure consistent ID field
                    }}
                    units={disciplineUnits}
                    onEdit={(ability?.can('update', 'Discipline') || ability?.can('manage', 'Discipline') || ability?.can('manage', 'all')) ? handleEditDiscipline : undefined}
                    onDelete={(ability?.can('delete', 'Discipline') || ability?.can('manage', 'Discipline') || ability?.can('manage', 'all')) ? handleDeleteDiscipline : undefined}
                    onClick={handleCardClick}
                    onCreateUnit={(ability?.can('create', 'Unit') || ability?.can('manage', 'Unit') || ability?.can('manage', 'all')) ? () => {
                      setSelectedDiscipline(discipline);
                      setOpenAddUnitDialog(true);
                    } : undefined}
                    onEditUnit={(ability?.can('update', 'Unit') || ability?.can('manage', 'Unit') || ability?.can('manage', 'all')) ? handleEditUnit : undefined}
                    onDeleteUnit={(ability?.can('delete', 'Unit') || ability?.can('manage', 'Unit') || ability?.can('manage', 'all')) ? handleDeleteUnit : undefined}
                    onClickUnit={(unit) => { }}
                  />
                </Box>
              );
            })
          )}
        </Box>

        {/* Pagination UI */}
        <PaginationUI
          paginationInfo={normalizedPaginationInfo}
          isLoading={isFetching}
          onPageChange={(page, rowsPerPage) => applyFilters(
            null,
            { currentPage: page, rowsPerPage }
          )}
          onRowsPerPageChange={(rowsPerPage) => applyFilters(
            null,
            { currentPage: 1, rowsPerPage }
          )}
        />

        {/* Loading indicator for pagination */}
        {isFetching && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
      </>
    );

    return showContent;
  };

  return (
    <Box p={3}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography sx={TitleScreen(colors)}>
            Departments & Units
          </Typography>
          <Typography sx={SubTitleScreen(colors)}>
            Manage departments and units according to your architecture
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          {(ability?.can('create', 'Unit') || ability?.can('manage', 'Unit') || ability?.can('manage', 'all')) && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => setOpenAddUnitDialog(true)}
            >
              Add Unit
            </Button>
          )}
          {(ability?.can('create', 'Discipline') || ability?.can('manage', 'Discipline') || ability?.can('manage', 'all')) && (
            <Button
              startIcon={<AddIcon />}
              onClick={() => setOpenAddDialog(true)}
              variant="contained"
              color="primary"
            >
              Add Department
            </Button>
          )}
        </Box>
      </Box>

      {/* Search Input */}
      <Box sx={{ mb: 3 }}>
        <SearchInput
          placeholder="Search departments..."
          value={searchTerm}
          onChange={(value) => {
            setSearchTerm(value);
            // If empty string, clear search
            if (value === '') {
              handleSearch('');
            }
          }}
          onSearch={handleSearch}
          width="100%"
          debounce={300}
        />
      </Box>

      {/* Global loading overlay */}
      <LoaderSpinner loading={isLoading || isFetching} />

      {renderContent()}

      {/* Add Discipline Dialog */}
      <AddDiscipline
        open={openAddDialog}
        onClose={() => setOpenAddDialog(false)}
        onSubmit={handleAddDiscipline}
      />

      {/* Add Unit Dialog */}
      <AddUnit
        open={openAddUnitDialog}
        onClose={() => setOpenAddUnitDialog(false)}
        disciplines={enhancedDisciplines}
        selectedDiscipline={selectedDiscipline}
        onSubmit={() => {
          // Refetch data after adding a unit
          refetch();
        }}
      />

      {/* Edit Discipline Dialog */}
      <UpdateDiscipline
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        discipline={selectedDiscipline}
        onSubmit={handleDisciplineUpdated}
      />

      {/* Delete Discipline Dialog */}
      <DeleteDiscipline
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        discipline={selectedDiscipline}
        onDeleted={handleDisciplineDeleted}
      />

      {/* Delete Unit Dialog */}
      <DeleteUnit
        open={openDeleteUnitDialog}
        onClose={() => setOpenDeleteUnitDialog(false)}
        unit={selectedUnit}
        onDeleted={handleUnitDeleted}
      />

      {/* Update Unit Dialog */}
      <UpdateUnit
        open={openUpdateUnitDialog}
        onClose={() => setOpenUpdateUnitDialog(false)}
        unit={selectedUnit}
        disciplines={enhancedDisciplines}
        onSubmit={handleUnitUpdated}
      />
    </Box>
  );
};

export default DisciplineList;